import { Html, OrbitControls, Stars, useGLTF } from '@react-three/drei';
import { Canvas, useFrame, useLoader, useThree } from '@react-three/fiber';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { ALIASES, H as ANIM_H, W as ANIM_W, DRAW, makeTransparentDraw } from './CountryAnimation';
import { COUNTRY_SCENES } from './CountryScene3D';
import { logClientError } from '../utils/errorLogger';
import useMediaQuery from '../utils/useMediaQuery';
import { API } from '../config';

const SPHERE_RADIUS = 2;

// ── World data ────────────────────────────────────────────────────────────────

const ISLANDS = [
  { name: 'Greenland',            lat: 72.0,  lng: -42.0  },
  { name: 'Iceland',              lat: 64.9,  lng: -19.0  },
  { name: 'Madagascar',           lat: -18.8, lng:  46.9  },
  { name: 'Sri Lanka',            lat:   7.9, lng:  80.7  },
  { name: 'Cuba',                 lat:  22.0, lng: -79.5  },
  { name: 'Borneo',               lat:   1.0, lng: 115.0  },
  { name: 'Sumatra',              lat:  -0.5, lng: 101.5  },
  { name: 'Java',                 lat:  -7.0, lng: 109.8  },
  { name: 'New Guinea',           lat:  -5.0, lng: 141.0  },
  { name: 'New Zealand (North)',  lat: -37.5, lng: 175.5  },
  { name: 'New Zealand (South)',  lat: -44.5, lng: 170.3  },
  { name: 'Hawaii',               lat:  19.6, lng: -155.6 },
  { name: 'Jamaica',              lat:  18.1, lng: -77.3  },
  { name: 'Hispaniola',           lat:  19.0, lng: -71.0  },
  { name: 'Puerto Rico',          lat:  18.2, lng: -66.5  },
  { name: 'Trinidad',             lat:  10.5, lng: -61.4  },
  { name: 'Maldives',             lat:   3.2, lng:  73.2  },
  { name: 'Seychelles',           lat:  -4.7, lng:  55.5  },
  { name: 'Réunion',              lat: -21.1, lng:  55.5  },
  { name: 'Mauritius',            lat: -20.2, lng:  57.5  },
  { name: 'Canary Islands',       lat:  28.3, lng: -16.5  },
  { name: 'Azores',               lat:  38.7, lng: -27.2  },
  { name: 'Faroe Islands',        lat:  62.0, lng:  -7.0  },
  { name: 'Tasmania',             lat: -42.0, lng: 147.0  },
  { name: 'Svalbard',             lat:  78.0, lng:  18.0  },
  { name: 'Cyprus',               lat:  35.1, lng:  33.4  },
  { name: 'Crete',                lat:  35.2, lng:  24.8  },
  { name: 'Corsica',              lat:  42.2, lng:   9.1  },
  { name: 'Sardinia',             lat:  40.1, lng:   9.1  },
  { name: 'Sicily',               lat:  37.6, lng:  14.0  },
  { name: 'Hokkaido',             lat:  43.5, lng: 142.9  },
  { name: 'Luzon',                lat:  16.0, lng: 121.0  },
  { name: 'Mindanao',             lat:   7.9, lng: 124.9  },
  { name: 'Sulawesi',             lat:  -1.5, lng: 121.0  },
  { name: 'Bali',                 lat:  -8.4, lng: 115.2  },
  { name: 'Zanzibar',             lat:  -6.2, lng:  39.4  },
  { name: 'Comoros',              lat: -11.9, lng:  43.3  },
  { name: 'Bermuda',              lat:  32.3, lng: -64.7  },
  { name: 'Barbados',             lat:  13.2, lng: -59.5  },
  { name: 'Martinique',           lat:  14.6, lng: -61.0  },
  { name: 'Guadeloupe',           lat:  16.3, lng: -61.4  },
  { name: 'Madeira',              lat:  32.8, lng: -17.0  },
  { name: 'Cape Verde',           lat:  16.0, lng: -24.0  },
  { name: 'Galápagos',            lat:  -0.5, lng: -90.5  },
  { name: 'Easter Island',        lat: -27.1, lng: -109.4 },
  { name: 'Fiji',                 lat: -18.0, lng: 178.0  },
  { name: 'Tahiti',               lat: -17.6, lng: -149.4 },
  { name: 'Samoa',                lat: -13.8, lng: -172.1 },
  { name: 'Tonga',                lat: -21.2, lng: -175.2 },
  { name: 'Vanuatu',              lat: -15.4, lng: 166.9  },
  { name: 'Solomon Islands',      lat:  -9.6, lng: 160.2  },
  { name: 'Timor-Leste',          lat:  -8.9, lng: 125.7  },
  { name: 'Socotra',              lat:  12.5, lng:  54.0  },
  { name: 'Andaman Islands',      lat:  11.7, lng:  92.7  },
];

// type → geometry + colour
const LANDMARKS = [
  // Pyramids & stepped temples
  { name: 'Great Pyramid of Giza',   lat:  29.98, lng:  31.13, type: 'pyramid'   },
  { name: 'Chichen Itza',            lat:  20.68, lng: -88.57, type: 'pyramid'   },
  { name: 'Teotihuacan',             lat:  19.69, lng: -98.84, type: 'pyramid'   },
  // Iconic towers & skyscrapers
  { name: 'Eiffel Tower',            lat:  48.86, lng:   2.29, type: 'tower'     },
  { name: 'Burj Khalifa',            lat:  25.20, lng:  55.27, type: 'tower'     },
  { name: 'CN Tower',                lat:  43.64, lng: -79.38, type: 'tower'     },
  { name: 'Tokyo Skytree',           lat:  35.71, lng: 139.81, type: 'tower'     },
  { name: 'Empire State Building',   lat:  40.75, lng: -73.99, type: 'tower'     },
  { name: 'Big Ben',                 lat:  51.50, lng:  -0.12, type: 'tower'     },
  { name: 'Leaning Tower of Pisa',   lat:  43.72, lng:  10.40, type: 'tower'     },
  { name: 'Sagrada Família',         lat:  41.40, lng:   2.17, type: 'tower'     },
  { name: 'Notre-Dame',              lat:  48.85, lng:   2.35, type: 'tower'     },
  { name: 'Petronas Towers',         lat:   3.16, lng: 101.71, type: 'tower'     },
  { name: 'One World Trade Center',  lat:  40.71, lng: -74.01, type: 'tower'     },
  { name: 'Shanghai Tower',          lat:  31.23, lng: 121.50, type: 'tower'     },
  // Domes & palaces
  { name: 'Taj Mahal',               lat:  27.17, lng:  78.04, type: 'dome'      },
  { name: 'Sydney Opera House',      lat: -33.86, lng: 151.21, type: 'dome'      },
  { name: 'Hagia Sophia',            lat:  41.01, lng:  28.98, type: 'dome'      },
  { name: "St. Peter's Basilica",    lat:  41.90, lng:  12.45, type: 'dome'      },
  { name: 'Kremlin',                 lat:  55.75, lng:  37.62, type: 'dome'      },
  { name: 'Blue Mosque',             lat:  41.00, lng:  28.97, type: 'dome'      },
  // Historic monuments
  { name: 'Great Wall of China',     lat:  40.43, lng: 116.57, type: 'wall'      },
  { name: 'Angkor Wat',              lat:  13.41, lng: 103.87, type: 'monument'  },
  { name: 'Petra',                   lat:  30.33, lng:  35.44, type: 'monument'  },
  { name: 'Stonehenge',              lat:  51.18, lng:  -1.83, type: 'monument'  },
  { name: 'Machu Picchu',            lat: -13.16, lng: -72.55, type: 'monument'  },
  { name: 'Colosseum',               lat:  41.89, lng:  12.49, type: 'monument'  },
  { name: 'Acropolis',               lat:  37.97, lng:  23.73, type: 'monument'  },
  { name: 'Alhambra',                lat:  37.18, lng:  -3.59, type: 'monument'  },
  { name: 'Forbidden City',          lat:  39.92, lng: 116.39, type: 'monument'  },
  { name: 'Palace of Versailles',    lat:  48.80, lng:   2.12, type: 'monument'  },
  { name: 'Borobudur',               lat:  -7.61, lng: 110.20, type: 'monument'  },
  { name: 'Moai (Easter Island)',    lat: -27.12, lng: -109.28,type: 'statue'    },
  // Statues
  { name: 'Statue of Liberty',       lat:  40.69, lng: -74.04, type: 'statue'    },
  { name: 'Christ the Redeemer',     lat: -22.95, lng: -43.21, type: 'statue'    },
  { name: 'Statue of Unity',         lat:  21.84, lng:  73.72, type: 'statue'    },
  // Mountains
  { name: 'Mount Everest',           lat:  27.99, lng:  86.93, type: 'mountain'  },
  { name: 'K2',                      lat:  35.88, lng:  76.51, type: 'mountain'  },
  { name: 'Kilimanjaro',             lat:  -3.07, lng:  37.35, type: 'mountain'  },
  { name: 'Mount Fuji',              lat:  35.36, lng: 138.73, type: 'mountain'  },
  { name: 'Denali',                  lat:  63.07, lng: -151.00,type: 'mountain'  },
  { name: 'Aconcagua',               lat: -32.65, lng: -70.01, type: 'mountain'  },
  { name: 'Mont Blanc',              lat:  45.83, lng:   6.86, type: 'mountain'  },
  { name: 'Table Mountain',          lat: -33.96, lng:  18.40, type: 'mountain'  },
  { name: 'Uluru',                   lat: -25.34, lng: 131.03, type: 'mountain'  },
  { name: 'Matterhorn',              lat:  45.98, lng:   7.66, type: 'mountain'  },
  { name: 'Mount Olympus',           lat:  40.09, lng:  22.36, type: 'mountain'  },
  { name: 'Vesuvius',                lat:  40.82, lng:  14.43, type: 'mountain'  },
  // Natural wonders
  { name: 'Grand Canyon',            lat:  36.06, lng: -112.14,type: 'canyon'    },
  { name: 'Niagara Falls',           lat:  43.08, lng: -79.07, type: 'waterfall' },
  { name: 'Victoria Falls',          lat: -17.92, lng:  25.86, type: 'waterfall' },
  { name: 'Angel Falls',             lat:   5.97, lng: -62.53, type: 'waterfall' },
  { name: 'Great Barrier Reef',      lat: -18.29, lng: 147.70, type: 'reef'      },
  { name: 'Amazon River Source',     lat:  -3.10, lng: -60.00, type: 'river'     },
  { name: 'Sahara Desert',           lat:  23.40, lng:   8.60, type: 'desert'    },
  { name: 'Patagonia',               lat: -50.00, lng: -72.00, type: 'mountain'  },
  { name: 'Dead Sea',                lat:  31.50, lng:  35.50, type: 'river'     },
  // Bridges
  { name: 'Golden Gate Bridge',      lat:  37.82, lng: -122.48,type: 'bridge'    },
  { name: 'Sydney Harbour Bridge',   lat: -33.85, lng: 151.21, type: 'bridge'    },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function latLngTo3D(lat, lng, r = SPHERE_RADIUS + 0.005) {
  const phi   = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -Math.sin(phi) * Math.cos(theta) * r,
     Math.cos(phi) * r,
     Math.sin(phi) * Math.sin(theta) * r,
  );
}

// Inverse of latLngTo3D — local-space point → geographic lat/lng
function localPointToLatLng(pt) {
  const n = pt.clone().normalize();
  const lat = 90 - Math.acos(Math.max(-1, Math.min(1, n.y))) * (180 / Math.PI);
  let lng   = Math.atan2(n.z, -n.x) * (180 / Math.PI) - 180;
  if (lng < -180) lng += 360;
  if (lng >  180) lng -= 360;
  return { lat, lng };
}

function nearestCountry(lat, lng, list) {
  return list.reduce((best, c) => {
    const d = (lat - c.lat) ** 2 + (lng - c.lng) ** 2;
    return d < best.d ? { d, c } : best;
  }, { d: Infinity, c: null }).c;
}

function buildBorders(geojson) {
  const pts = [];
  const addRing = ring => {
    for (let i = 0; i < ring.length - 1; i++) {
      // Sit just above the stylized globe's raised land (r × 1.018)
      const v1 = latLngTo3D(ring[i][1], ring[i][0], SPHERE_RADIUS * 1.022);
      const v2 = latLngTo3D(ring[i + 1][1], ring[i + 1][0], SPHERE_RADIUS * 1.022);
      pts.push(v1.x, v1.y, v1.z, v2.x, v2.y, v2.z);
    }
  };
  for (const f of geojson.features) {
    const g = f.geometry;
    if (!g) continue;
    if (g.type === 'Polygon')      g.coordinates.forEach(addRing);
    if (g.type === 'MultiPolygon') g.coordinates.forEach(p => p.forEach(addRing));
  }
  return new Float32Array(pts);
}

// Colour per landmark category
const LANDMARK_COLOR = {
  pyramid:   '#FFD700',
  tower:     '#87CEEB',
  dome:      '#DDA0DD',
  mountain:  '#CD853F',
  statue:    '#C0C0C0',
  monument:  '#FFA07A',
  bridge:    '#40E0D0',
  waterfall: '#00BFFF',
  canyon:    '#E07030',
  reef:      '#00FF7F',
  river:     '#1E90FF',
  desert:    '#F4A460',
  wall:      '#BC8F5F',
};

// Quaternion that stands an object "upright" at a lat/lng on the sphere
function surfaceQuaternion(lat, lng) {
  const normal = latLngTo3D(lat, lng, 1).normalize();
  return new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), normal);
}

// ── Scene sub-components ──────────────────────────────────────────────────────

function Borders({ verts }) {
  const geo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(verts, 3));
    return g;
  }, [verts]);
  return (
    <lineSegments geometry={geo}>
      <lineBasicMaterial color="#4488ee" transparent opacity={0.22} />
    </lineSegments>
  );
}

function CameraZoom({ target, active }) {
  const { camera } = useThree();
  const animating  = useRef(false);
  const dest       = useRef(new THREE.Vector3(0, 0, 6));

  useEffect(() => {
    if (active && target) {
      dest.current = target.clone().normalize().multiplyScalar(3.3);
    } else {
      dest.current = new THREE.Vector3(0, 0, 6);
    }
    animating.current = true;
  }, [active, target]);

  useFrame((_, dt) => {
    if (!animating.current) return;
    camera.position.lerp(dest.current, Math.min(dt * 3, 1));
    camera.lookAt(0, 0, 0);
    if (camera.position.distanceTo(dest.current) < 0.05) {
      camera.position.copy(dest.current);
      animating.current = false;
    }
  });

  return null;
}

// ── Click catcher ─────────────────────────────────────────────────────────────
// Invisible sphere OUTSIDE Suspense — always hittable, even while texture loads.
// Uses world-space hit + earthRotRef to undo the globe's auto-rotation before
// computing geographic lat/lng.

function ClickCatcher({ countries, onPick, earthRotRef, zoomed }) {
  const downXY = useRef(null);

  const handlePointerDown = useCallback(e => {
    downXY.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handlePointerUp = useCallback(e => {
    if (!downXY.current || !countries.length || zoomed) return;
    const dx = e.clientX - downXY.current.x;
    const dy = e.clientY - downXY.current.y;
    downXY.current = null;
    if (dx * dx + dy * dy > 36) return; // was a drag

    e.stopPropagation();

    const localPt = e.point
      .clone()
      .applyAxisAngle(new THREE.Vector3(0, 1, 0), -(earthRotRef.current || 0));

    const { lat, lng } = localPointToLatLng(localPt);
    const country = nearestCountry(lat, lng, countries);
    if (country) onPick(country, e.point.clone());
  }, [countries, onPick, earthRotRef, zoomed]);

  return (
    <mesh onPointerDown={handlePointerDown} onPointerUp={handlePointerUp}>
      <sphereGeometry args={[SPHERE_RADIUS + 0.01, 64, 32]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  );
}

// ── Earth visuals (suspends until texture ready) ──────────────────────────────

function EarthVisuals({ zoomed, earthRotRef }) {
  const meshRef  = useRef();
  const [borders, setBorders] = useState(null);
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  // Stylized low-poly earth (Blender-authored, vertex-colored, Draco-compressed).
  // Radius 1 in the GLB — scaled up to SPHERE_RADIUS below. Its lon/lat layout
  // matches latLngTo3D exactly (Greenwich → +X, 90°E → −Z), so picking,
  // borders, and markers need no rotation offset.
  const { scene } = useGLTF(`${API}/models/stylized-earth.glb`);

  const globe = useMemo(() => {
    const g = scene.clone(true);
    g.traverse(o => {
      if (o.isMesh) {
        o.material.roughness = 0.95;
        o.material.metalness = 0;
      }
    });
    return g;
  }, [scene]);

  useEffect(() => {
    fetch(`${API}/geo/countries.geojson`)
      .then(r => r.json())
      .then(geo => setBorders(buildBorders(geo)))
      .catch(() => {});
  }, []);

  useFrame((_, dt) => {
    // Rotate globe when not zoomed
    if (meshRef.current && !zoomed && !reducedMotion) {
      meshRef.current.rotation.y += dt * 0.04;
      earthRotRef.current = meshRef.current.rotation.y;
    }
  });

  return (
    <group ref={meshRef}>
      <primitive object={globe} scale={[SPHERE_RADIUS, SPHERE_RADIUS, SPHERE_RADIUS]} />
      {borders && <Borders verts={borders} />}
    </group>
  );
}

// Start fetching the globe before first render — shortens the fallback flash.
useGLTF.preload(`${API}/models/stylized-earth.glb`);
useGLTF.preload(`${API}/models/beary-plane.glb`);

// ── Beary's plane — flight mode ───────────────────────────────────────────────
// Rendered inside the markers group so it shares the earth's rotation frame;
// lat/lng math therefore matches picking + landing exactly.
function PlaneRider({ geoRef, onLand }) {
  const holder = useRef();
  const { scene } = useGLTF(`${API}/models/beary-plane.glb`);
  const model = useMemo(() => scene.clone(true), [scene]);
  const { camera } = useThree();
  const keys = useRef({});

  useEffect(() => {
    // start over the Atlantic heading east so a continent appears fast
    geoRef.current = { lat: 24, lng: -35, heading: 95 };
    const dn = e => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'a' || e.key === 'd') {
        keys.current[e.key] = true;
        e.preventDefault();
      }
      if (e.code === 'Space') {
        e.preventDefault();
        onLand();
      }
    };
    const up = e => { keys.current[e.key] = false; };
    window.addEventListener('keydown', dn);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', dn);
      window.removeEventListener('keyup', up);
    };
  }, [geoRef, onLand]);

  useFrame((_, dt) => {
    const g = geoRef.current;
    if (!g || !holder.current) return;

    const turn = 95 * dt;
    if (keys.current.ArrowLeft || keys.current.a) g.heading -= turn;
    if (keys.current.ArrowRight || keys.current.d) g.heading += turn;

    const spd = 17 * dt; // degrees of arc per second
    const hr = (g.heading * Math.PI) / 180;
    const latStretch = Math.max(0.25, Math.cos((g.lat * Math.PI) / 180));
    g.lat += Math.cos(hr) * spd;
    g.lng += (Math.sin(hr) * spd) / latStretch;
    // bounce off the poles, wrap the date line
    if (g.lat > 74)  { g.lat = 74;  g.heading = 180 - g.heading; }
    if (g.lat < -74) { g.lat = -74; g.heading = 180 - g.heading; }
    if (g.lng > 180)  g.lng -= 360;
    if (g.lng < -180) g.lng += 360;

    const R = SPHERE_RADIUS * 1.07;
    const pos = latLngTo3D(g.lat, g.lng, R);
    const aheadLat = g.lat + Math.cos(hr) * 2.5;
    const aheadLng = g.lng + (Math.sin(hr) * 2.5) / latStretch;
    const ahead = latLngTo3D(aheadLat, aheadLng, R);

    const h = holder.current;
    h.position.copy(pos);
    h.up.copy(pos.clone().normalize());
    const aheadWorld = ahead.clone();
    h.parent.localToWorld(aheadWorld);
    h.lookAt(aheadWorld);

    // chase camera — hover outside the plane's spot on the globe
    const posWorld = pos.clone();
    h.parent.localToWorld(posWorld);
    const camDest = posWorld.clone().normalize().multiplyScalar(SPHERE_RADIUS * 2.35);
    camera.position.lerp(camDest, Math.min(dt * 2.8, 1));
    camera.lookAt(posWorld);
  });

  return (
    <group ref={holder}>
      {/* model nose points +X; rotate so it faces the group's -Z look direction */}
      <primitive object={model} scale={[0.16, 0.16, 0.16]} rotation={[0, Math.PI / 2, 0]} />
    </group>
  );
}

// ── Island marker ─────────────────────────────────────────────────────────────

function IslandDot({ name, lat, lng }) {
  const [hovered, setHovered] = useState(false);
  const [hasGlb,  setHasGlb]  = useState(false);
  const glbInfo = ISLAND_GLB_FILES[name];

  const { pos, quat } = useMemo(() => {
    const p = latLngTo3D(lat, lng, SPHERE_RADIUS);
    const q = new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 1, 0),
      p.clone().normalize()
    );
    return { pos: p, quat: q };
  }, [lat, lng]);

  const stopP = useCallback(e => e.stopPropagation(), []);

  // Check if GLB exists on backend
  useEffect(() => {
    if (!glbInfo) return;
    fetch(`${API}/models/${glbInfo.file}`, { method: 'HEAD' })
      .then(r => { if (r.ok) setHasGlb(true); })
      .catch(() => {});
  }, [glbInfo]);

  if (glbInfo && hasGlb) {
    // 3D island model — upright on globe surface
    return (
      <group
        position={pos}
        quaternion={quat}
        onPointerOver={e => { e.stopPropagation(); setHovered(true); }}
        onPointerOut={() => setHovered(false)}
        onPointerDown={stopP}
        onPointerUp={stopP}
      >
        <Suspense fallback={null}>
          <GLTFMesh url={`${API}/models/${glbInfo.file}`} scale={glbInfo.scale} />
        </Suspense>
        {hovered && (
          <Html distanceFactor={8} center>
            <div style={islandLabel}>{name}</div>
          </Html>
        )}
      </group>
    );
  }

  // Fallback — cyan dot
  return (
    <mesh
      position={pos}
      onPointerOver={e => { e.stopPropagation(); setHovered(true); }}
      onPointerOut={() => setHovered(false)}
      onPointerDown={stopP}
      onPointerUp={stopP}
    >
      <sphereGeometry args={[0.011, 7, 7]} />
      <meshStandardMaterial
        color={hovered ? '#ffffff' : '#00E5CC'}
        emissive="#00E5CC"
        emissiveIntensity={hovered ? 1.8 : 0.7}
      />
      {hovered && (
        <Html distanceFactor={8} center>
          <div style={islandLabel}>{name}</div>
        </Html>
      )}
    </mesh>
  );
}

// ── GLB model mapping ─────────────────────────────────────────────────────────
// Drop a .glb file into backend/public/models/ and add its entry here.
// The globe will automatically use the 3D model instead of the fallback pin.
// Download sources: poly.pizza (no login), sketchfab.com/features/free
//
// poly.pizza pages (one-click download, no login):
//   Eiffel Tower   → https://poly.pizza/m/dJA7R5pOFK
//   Colosseum      → https://poly.pizza/m/83ftBPyiSf
//   Stonehenge     → https://poly.pizza/m/eUTU5GlbzS
//   Pyramid        → https://poly.pizza/m/9UmATxXbBs
//   Pagoda tower   → https://poly.pizza/m/3Oa5QgdtKf
//   Lighthouse     → https://poly.pizza/m/7nXpZ1mKe8

// GLB models generated in Blender — all live in backend/public/models/
const GLB_FILES = {
  // ── Original 14 ──────────────────────────────────────────────────────────
  'Eiffel Tower':            { file: 'eiffel-tower.glb',         scale: 0.060 },
  'Colosseum':               { file: 'colosseum.glb',             scale: 0.055 },
  'Stonehenge':              { file: 'stonehenge.glb',            scale: 0.055 },
  'Great Pyramid of Giza':   { file: 'pyramid-giza.glb',         scale: 0.060 },
  'Taj Mahal':               { file: 'taj-mahal.glb',             scale: 0.055 },
  'Statue of Liberty':       { file: 'statue-liberty.glb',       scale: 0.060 },
  'Burj Khalifa':            { file: 'burj-khalifa.glb',         scale: 0.050 },
  'Golden Gate Bridge':      { file: 'golden-gate.glb',          scale: 0.055 },
  'Sydney Opera House':      { file: 'sydney-opera.glb',         scale: 0.060 },
  'Christ the Redeemer':     { file: 'christ-redeemer.glb',      scale: 0.060 },
  'Big Ben':                 { file: 'big-ben.glb',               scale: 0.060 },
  'Mount Fuji':              { file: 'mount-fuji.glb',            scale: 0.055 },
  'Machu Picchu':            { file: 'machu-picchu.glb',          scale: 0.055 },
  'Angkor Wat':              { file: 'angkor-wat.glb',            scale: 0.055 },
  // ── Pyramids & temples ────────────────────────────────────────────────────
  'Chichen Itza':            { file: 'chichen-itza.glb',          scale: 0.055 },
  'Teotihuacan':             { file: 'teotihuacan.glb',           scale: 0.055 },
  // ── Towers & skyscrapers ─────────────────────────────────────────────────
  'CN Tower':                { file: 'cn-tower.glb',              scale: 0.048 },
  'Tokyo Skytree':           { file: 'tokyo-skytree.glb',         scale: 0.048 },
  'Empire State Building':   { file: 'empire-state.glb',          scale: 0.052 },
  'Leaning Tower of Pisa':   { file: 'leaning-tower-pisa.glb',   scale: 0.052 },
  'Sagrada Família':         { file: 'sagrada-familia.glb',       scale: 0.052 },
  'Notre-Dame':              { file: 'notre-dame.glb',             scale: 0.055 },
  'Petronas Towers':         { file: 'petronas-towers.glb',       scale: 0.050 },
  'One World Trade Center':  { file: 'one-world-trade.glb',       scale: 0.050 },
  'Shanghai Tower':          { file: 'shanghai-tower.glb',        scale: 0.050 },
  // ── Domes & mosques ───────────────────────────────────────────────────────
  'Hagia Sophia':            { file: 'hagia-sophia.glb',          scale: 0.055 },
  "St. Peter's Basilica":    { file: 'st-peters-basilica.glb',   scale: 0.055 },
  'Kremlin':                 { file: 'kremlin.glb',               scale: 0.055 },
  'Blue Mosque':             { file: 'blue-mosque.glb',           scale: 0.052 },
  // ── Monuments & historic ─────────────────────────────────────────────────
  'Great Wall of China':     { file: 'great-wall.glb',            scale: 0.055 },
  'Petra':                   { file: 'petra.glb',                 scale: 0.055 },
  'Acropolis':               { file: 'acropolis.glb',             scale: 0.055 },
  'Alhambra':                { file: 'alhambra.glb',              scale: 0.055 },
  'Forbidden City':          { file: 'forbidden-city.glb',        scale: 0.055 },
  'Palace of Versailles':    { file: 'palace-versailles.glb',     scale: 0.055 },
  'Borobudur':               { file: 'borobudur.glb',             scale: 0.055 },
  // ── Statues ───────────────────────────────────────────────────────────────
  'Moai (Easter Island)':    { file: 'moai.glb',                  scale: 0.055 },
  'Statue of Unity':         { file: 'statue-unity.glb',          scale: 0.050 },
  // ── Mountains ────────────────────────────────────────────────────────────
  'Mount Everest':           { file: 'mount-everest.glb',         scale: 0.055 },
  'K2':                      { file: 'k2.glb',                    scale: 0.055 },
  'Kilimanjaro':             { file: 'kilimanjaro.glb',           scale: 0.055 },
  'Denali':                  { file: 'denali.glb',                scale: 0.055 },
  'Aconcagua':               { file: 'aconcagua.glb',             scale: 0.055 },
  'Mont Blanc':              { file: 'mont-blanc.glb',            scale: 0.055 },
  'Table Mountain':          { file: 'table-mountain.glb',        scale: 0.055 },
  'Uluru':                   { file: 'uluru.glb',                 scale: 0.055 },
  'Matterhorn':              { file: 'matterhorn.glb',            scale: 0.055 },
  'Mount Olympus':           { file: 'mount-olympus.glb',         scale: 0.055 },
  'Vesuvius':                { file: 'vesuvius.glb',              scale: 0.055 },
  'Patagonia':               { file: 'patagonia.glb',             scale: 0.055 },
  // ── Natural wonders ───────────────────────────────────────────────────────
  'Grand Canyon':            { file: 'grand-canyon.glb',          scale: 0.055 },
  'Niagara Falls':           { file: 'niagara-falls.glb',         scale: 0.055 },
  'Victoria Falls':          { file: 'victoria-falls.glb',        scale: 0.055 },
  'Angel Falls':             { file: 'angel-falls.glb',           scale: 0.055 },
  'Great Barrier Reef':      { file: 'great-barrier-reef.glb',   scale: 0.055 },
  'Amazon River Source':     { file: 'amazon-river.glb',          scale: 0.055 },
  'Sahara Desert':           { file: 'sahara-desert.glb',         scale: 0.055 },
  'Dead Sea':                { file: 'dead-sea.glb',              scale: 0.055 },
  // ── Bridges ───────────────────────────────────────────────────────────────
  'Sydney Harbour Bridge':   { file: 'sydney-harbour-bridge.glb', scale: 0.055 },
};

// ── Island GLB models ─────────────────────────────────────────────────────────
const ISLAND_GLB_FILES = {
  'Greenland':           { file: 'greenland.glb',          scale: 0.060 },
  'Iceland':             { file: 'iceland.glb',            scale: 0.058 },
  'Madagascar':          { file: 'madagascar.glb',         scale: 0.058 },
  'Sri Lanka':           { file: 'sri-lanka.glb',          scale: 0.055 },
  'Cuba':                { file: 'cuba.glb',               scale: 0.058 },
  'Borneo':              { file: 'borneo.glb',             scale: 0.060 },
  'Sumatra':             { file: 'sumatra.glb',            scale: 0.058 },
  'Java':                { file: 'java.glb',               scale: 0.058 },
  'New Guinea':          { file: 'new-guinea.glb',         scale: 0.060 },
  'New Zealand (North)': { file: 'new-zealand-north.glb',  scale: 0.058 },
  'New Zealand (South)': { file: 'new-zealand-south.glb',  scale: 0.058 },
  'Hawaii':              { file: 'hawaii.glb',             scale: 0.055 },
  'Jamaica':             { file: 'jamaica.glb',            scale: 0.053 },
  'Hispaniola':          { file: 'hispaniola.glb',         scale: 0.055 },
  'Puerto Rico':         { file: 'puerto-rico.glb',        scale: 0.053 },
  'Trinidad':            { file: 'trinidad.glb',           scale: 0.053 },
  'Maldives':            { file: 'maldives.glb',           scale: 0.050 },
  'Seychelles':          { file: 'seychelles.glb',         scale: 0.050 },
  'Réunion':             { file: 'reunion.glb',            scale: 0.050 },
  'Mauritius':           { file: 'mauritius.glb',          scale: 0.050 },
  'Canary Islands':      { file: 'canary-islands.glb',     scale: 0.053 },
  'Azores':              { file: 'azores.glb',             scale: 0.050 },
  'Faroe Islands':       { file: 'faroe-islands.glb',      scale: 0.050 },
  'Tasmania':            { file: 'tasmania.glb',           scale: 0.055 },
  'Svalbard':            { file: 'svalbard.glb',           scale: 0.055 },
  'Cyprus':              { file: 'cyprus.glb',             scale: 0.053 },
  'Crete':               { file: 'crete.glb',              scale: 0.055 },
  'Corsica':             { file: 'corsica.glb',            scale: 0.053 },
  'Sardinia':            { file: 'sardinia.glb',           scale: 0.053 },
  'Sicily':              { file: 'sicily.glb',             scale: 0.055 },
  'Hokkaido':            { file: 'hokkaido.glb',           scale: 0.058 },
  'Luzon':               { file: 'luzon.glb',              scale: 0.055 },
  'Mindanao':            { file: 'mindanao.glb',           scale: 0.055 },
  'Sulawesi':            { file: 'sulawesi.glb',           scale: 0.055 },
  'Bali':                { file: 'bali.glb',               scale: 0.053 },
  'Zanzibar':            { file: 'zanzibar.glb',           scale: 0.050 },
  'Comoros':             { file: 'comoros.glb',            scale: 0.050 },
  'Bermuda':             { file: 'bermuda.glb',            scale: 0.048 },
  'Barbados':            { file: 'barbados.glb',           scale: 0.048 },
  'Martinique':          { file: 'martinique.glb',         scale: 0.048 },
  'Guadeloupe':          { file: 'guadeloupe.glb',         scale: 0.050 },
  'Madeira':             { file: 'madeira.glb',            scale: 0.048 },
  'Cape Verde':          { file: 'cape-verde.glb',         scale: 0.050 },
  'Galápagos':           { file: 'galapagos.glb',          scale: 0.053 },
  'Easter Island':       { file: 'easter-island.glb',      scale: 0.050 },
  'Fiji':                { file: 'fiji.glb',               scale: 0.053 },
  'Tahiti':              { file: 'tahiti.glb',             scale: 0.050 },
  'Samoa':               { file: 'samoa.glb',              scale: 0.050 },
  'Tonga':               { file: 'tonga.glb',              scale: 0.048 },
  'Vanuatu':             { file: 'vanuatu.glb',            scale: 0.050 },
  'Solomon Islands':     { file: 'solomon-islands.glb',    scale: 0.053 },
  'Timor-Leste':         { file: 'timor-leste.glb',        scale: 0.053 },
  'Socotra':             { file: 'socotra.glb',            scale: 0.050 },
  'Andaman Islands':     { file: 'andaman-islands.glb',    scale: 0.050 },
};

// ── GLTF mesh (loaded from backend /models/) ──────────────────────────────────
function GLTFMesh({ url, scale }) {
  const { scene } = useGLTF(url);
  // Clone so multiple instances don't share state
  const clone = useMemo(() => scene.clone(true), [scene]);
  return <primitive object={clone} scale={[scale, scale, scale]} />;
}

// ── DALL-E sprite (camera-facing billboard) ───────────────────────────────────
// The image is generated with a transparent background (see
// services/landmarkImage.js), so only the landmark silhouette is composited
// onto the globe — no white card. The high alphaTest cuts any residual near-
// white halo some image models still emit around the subject.
function SpriteMesh({ url }) {
  const tex = useLoader(THREE.TextureLoader, url);
  return (
    <sprite scale={[0.08, 0.08, 0.08]}>
      <spriteMaterial
        map={tex}
        transparent
        alphaTest={0.35}
        depthWrite={false}
        sizeAttenuation
        toneMapped={false}
      />
    </sprite>
  );
}

// ── Fallback pin (type-based geometry, always visible) ────────────────────────
function FallbackPin({ type, col, emI }) {
  const PIN_H = 0.030;
  return (
    <mesh>
      {type === 'pyramid'   && <coneGeometry     args={[0.014, PIN_H,        4]} />}
      {type === 'tower'     && <cylinderGeometry args={[0.003, 0.006, PIN_H + 0.010, 6]} />}
      {type === 'mountain'  && <coneGeometry     args={[0.013, PIN_H + 0.004, 6]} />}
      {type === 'dome'      && <sphereGeometry   args={[0.013, 8, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />}
      {type === 'statue'    && <cylinderGeometry args={[0.004, 0.007, PIN_H,  8]} />}
      {type === 'bridge'    && <boxGeometry      args={[0.038, 0.004, 0.006]} />}
      {type === 'wall'      && <boxGeometry      args={[0.040, 0.006, 0.005]} />}
      {type === 'waterfall' && <cylinderGeometry args={[0.006, 0.010, PIN_H,  8]} />}
      {type === 'canyon'    && <boxGeometry      args={[0.022, 0.018, 0.010]} />}
      {type === 'reef'      && <torusGeometry    args={[0.012, 0.004, 6, 10]} />}
      {type === 'river'     && <torusGeometry    args={[0.010, 0.003, 6, 10]} />}
      {type === 'desert'    && <coneGeometry     args={[0.014, 0.018,       3]} />}
      {!['pyramid','tower','mountain','dome','statue','bridge','wall',
          'waterfall','canyon','reef','river','desert'].includes(type) &&
        <octahedronGeometry args={[0.012]} />}
      <meshStandardMaterial
        color={col} emissive={col} emissiveIntensity={emI}
        roughness={0.3} metalness={0.4}
      />
    </mesh>
  );
}

// ── Landmark pin — GLB model OR fallback pin, with DALL-E sprite on hover ─────
function LandmarkPin({ name, lat, lng, type, onLandmarkClick }) {
  const [hovered,         setHovered]         = useState(false);
  const [spriteUrl,       setSpriteUrl]       = useState(null);
  const [spriteLoading,   setSpriteLoading]   = useState(false);
  const [hasGlb,          setHasGlb]          = useState(false);
  const downXY = useRef(null);

  const col     = LANDMARK_COLOR[type] || '#FFD700';
  const glbInfo = GLB_FILES[name];

  // ── Check whether a downloaded GLB exists on the backend ─────────────────
  useEffect(() => {
    if (!glbInfo) return;
    fetch(`${API}/models/${glbInfo.file}`, { method: 'HEAD' })
      .then(r => { if (r.ok) setHasGlb(true); })
      .catch(() => {});
  }, [glbInfo]);

  // ── Fetch DALL-E sprite after a sustained hover (cached by MongoDB) ───────
  // Deliberately debounced: a cursor sweeping across the globe brushes dozens
  // of pins, and each un-debounced fetch was an API call (rate-limit burn) —
  // or a PAID generation on cache miss. 350ms means "actually hovering".
  // Tablet taps never hover, so this path doesn't affect touch at all.
  useEffect(() => {
    if (!hovered || spriteUrl || spriteLoading) return undefined;
    const t = setTimeout(() => {
      setSpriteLoading(true);
      fetch(`${API}/api/landmark/${encodeURIComponent(name)}`)
        .then(r => r.json())
        .then(d => { if (d.imageUrl) setSpriteUrl(`${API}${d.imageUrl}`); })
        .catch(() => {})
        .finally(() => setSpriteLoading(false));
    }, 350);
    return () => clearTimeout(t);
  }, [hovered, name, spriteUrl, spriteLoading]);

  const { pos, quat } = useMemo(() => ({
    // ×1.018 keeps landmark bases on top of the stylized globe's raised land
    pos:  latLngTo3D(lat, lng, SPHERE_RADIUS * 1.018),
    quat: surfaceQuaternion(lat, lng),
  }), [lat, lng]);

  const onOver = useCallback(e => { e.stopPropagation(); setHovered(true);  }, []);
  const onOut  = useCallback(()  => setHovered(false), []);

  const onDown = useCallback(e => {
    e.stopPropagation();
    downXY.current = { x: e.clientX, y: e.clientY };
  }, []);

  const onUp = useCallback(e => {
    e.stopPropagation();
    if (!downXY.current) return;
    const dx = e.clientX - downXY.current.x;
    const dy = e.clientY - downXY.current.y;
    downXY.current = null;
    if (dx * dx + dy * dy > 36) return; // was a drag — ignore
    if (onLandmarkClick) onLandmarkClick(name);
  }, [name, onLandmarkClick]);

  return (
    <group position={pos} quaternion={quat}>

      {/* ── 3D object: GLB if available, else type-based shape ── */}
      {hasGlb ? (
        <Suspense fallback={<FallbackPin type={type} col={col} emI={0.5} />}>
          <GLTFMesh url={`${API}/models/${glbInfo.file}`} scale={glbInfo.scale} />
        </Suspense>
      ) : (
        <FallbackPin type={type} col={col} emI={hovered ? 1.4 : 0.55} />
      )}

      {/* ── DALL-E sprite: small top-down miniature, ONLY while hovered ── */}
      {hovered && spriteUrl && (
        <group position={[0, 0.05, 0]}>
          <Suspense fallback={null}>
            <SpriteMesh url={spriteUrl} />
          </Suspense>
        </group>
      )}

      {/* ── Invisible hitbox for reliable pointer events ── */}
      <mesh
        position={[0, 0.05, 0]}
        onPointerOver={onOver}
        onPointerOut={onOut}
        onPointerDown={onDown}
        onPointerUp={onUp}
      >
        <sphereGeometry args={[0.12, 6, 6]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>



    </group>
  );
}

// ── Label styles ──────────────────────────────────────────────────────────────

const islandLabel = {
  background: 'rgba(0,180,160,0.88)',
  color: '#fff',
  padding: '2px 8px',
  borderRadius: 6,
  fontSize: 11,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontWeight: 600,
  letterSpacing: '-0.005em',
  boxShadow: '0 1px 6px rgba(0,0,0,0.6)',
};

// eslint-disable-next-line no-unused-vars
const landmarkLabel = color => ({
  background: 'rgba(0,0,0,0.80)',
  color,
  padding: '2px 8px',
  borderRadius: 6,
  fontSize: 11,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontWeight: 700,
  letterSpacing: '-0.005em',
  border: `1px solid ${color}`,
  boxShadow: '0 1px 6px rgba(0,0,0,0.6)',
});

// ── World markers (islands + landmarks) ──────────────────────────────────────

function WorldMarkers({ onLandmarkClick }) {
  return (
    <>
      {ISLANDS.map(island => (
        <IslandDot key={island.name} {...island} />
      ))}
      {LANDMARKS.map(lm => (
        <LandmarkPin key={`${lm.name}-${lm.lat}`} {...lm} onLandmarkClick={onLandmarkClick} />
      ))}
    </>
  );
}

// ── Inline R3F country scenes ─────────────────────────────────────────────────
// COUNTRY_SCENES maps country names to self-contained R3F scene components.
// Each scene is built at ~4-unit natural scale; we apply a uniform scale of
// 0.15 here so the scene fits neatly on the globe surface (radius 2).

const SCENE_SCALE = 0.20;
const SCENE_ELEVATE = 0.02; // offset above sphere so characters sit on surface

// GLB dioramas authored in Blender — the scalable path for country scenes.
// Drop a .glb into backend/public/models/scenes/ and add an entry here.
// Build at the same natural scale as the procedural scenes (ground disc
// radius ≈ 2.8 units, Y-up); SCENE_SCALE × entry scale is applied on top —
// tune `scale` per scene until it reads clearly at the zoomed camera distance.
const GLB_SCENES = {
  India:         { file: 'scenes/india.glb',     scale: 2.5 }, // Taj Mahal gardens
  Brazil:        { file: 'scenes/brazil.glb',    scale: 2.5 }, // Christ the Redeemer
  France:        { file: 'scenes/france.glb',    scale: 2.5 }, // Eiffel Tower
  'United States of America': { file: 'scenes/usa.glb', scale: 2.5 }, // Golden Gate (globe tap)
  'United States':            { file: 'scenes/usa.glb', scale: 2.5 }, // Golden Gate (search)
  China:         { file: 'scenes/china.glb',     scale: 2.5 }, // Great Wall
  Australia:     { file: 'scenes/australia.glb', scale: 2.5 }, // Opera House + Uluru
  Mexico:        { file: 'scenes/mexico.glb',    scale: 2.5 }, // Chichen Itza
  Italy:         { file: 'scenes/italy.glb',     scale: 2.5 }, // Leaning Tower of Pisa
  Greece:        { file: 'scenes/greece.glb',    scale: 2.5 }, // Parthenon
  'United Kingdom': { file: 'scenes/uk.glb',     scale: 2.5 }, // Big Ben + red bus
  Russia:        { file: 'scenes/russia.glb',    scale: 2.5 }, // St Basil's Cathedral
};

// Positions a scene component flat on the globe surface, oriented outward.
function CountryScene3DOverlay({ point, countryName }) {
  const SceneComponent = COUNTRY_SCENES[countryName] ?? null;
  const glbEntry = !SceneComponent ? GLB_SCENES[countryName] : null;

  // All hooks must run unconditionally — guard after hooks.
  const norm = useMemo(() => point.clone().normalize(), [point]);
  const pos  = useMemo(
    () => norm.clone().multiplyScalar(SPHERE_RADIUS + SCENE_ELEVATE),
    [norm]
  );
  const quat = useMemo(
    () => new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 1, 0),
      norm
    ),
    [norm]
  );

  if (!SceneComponent && !glbEntry) return null;

  return (
    <group position={pos} quaternion={quat} scale={[SCENE_SCALE, SCENE_SCALE, SCENE_SCALE]}>
      {SceneComponent ? (
        <SceneComponent />
      ) : (
        <Suspense fallback={null}>
          <GLTFMesh url={`${API}/models/${glbEntry.file}`} scale={glbEntry.scale || 1} />
        </Suspense>
      )}
    </group>
  );
}

// ── Country animation overlay — painted flat on the globe surface ─────────────
// Instead of an <Html> billboard that floats in front of the camera, we:
//   1. Create an offscreen <canvas> and run the same CountryAnimation draw loop
//   2. Stream it each frame into a THREE.CanvasTexture
//   3. Map that texture onto a PlaneGeometry whose normal faces outward along
//      the surface — so it lies flat on the land like a painted mural.

function CountryOverlay({ point, countryName }) {
  const texRef = useRef(null);
  const [tex, setTex] = useState(null);

  // Resolve country name → wrapped draw function with transparent background.
  // makeTransparentDraw skips the full-canvas gbg() fill so the globe texture
  // shows through; the animated characters/elements still render normally.
  const drawFn = useMemo(() => {
    const resolved = ALIASES[countryName] !== undefined ? ALIASES[countryName] : countryName;
    const fn = resolved ? DRAW[resolved] : null;
    return fn ? makeTransparentDraw(fn) : null;
  }, [countryName]);

  // Position: flush with the surface (tiny epsilon so it doesn't z-fight)
  // Quaternion: rotate the plane's +Z axis to match the outward surface normal,
  // so the plane lies flat on the globe instead of standing upright.
  const { pos, quat } = useMemo(() => {
    const norm = point.clone().normalize();
    const pos  = norm.clone().multiplyScalar(SPHERE_RADIUS + 0.008);
    const quat = new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 0, 1), // PlaneGeometry's default face direction
      norm                         // we want that face to point straight outward
    );
    return { pos, quat };
  }, [point]);

  // Create an offscreen canvas, start the animation loop, feed it to a texture
  useEffect(() => {
    if (!drawFn) return;

    const canvas  = document.createElement('canvas');
    canvas.width  = ANIM_W;
    canvas.height = ANIM_H;
    const ctx   = canvas.getContext('2d');
    const start = Date.now();
    let rafId;

    const loop = () => {
      const t = (Date.now() - start) / 1000;
      ctx.clearRect(0, 0, ANIM_W, ANIM_H);
      drawFn(ctx, t);

      // ── Soft vignette — dissolves hard rectangular edges into transparency ──
      // Uses destination-in: keeps pixels where the gradient is opaque,
      // fades them where it transitions, erases where fully transparent.
      // The gradient radius equals half the canvas width so its circle clips
      // the taller dimension first, producing a natural horizontal oval shape.
      ctx.globalCompositeOperation = 'destination-in';
      const cx = ANIM_W / 2, cy = ANIM_H / 2, r = ANIM_W / 2;
      const vignette = ctx.createRadialGradient(cx, cy, r * 0.30, cx, cy, r);
      vignette.addColorStop(0,    'rgba(0,0,0,1)');   // fully opaque centre
      vignette.addColorStop(0.55, 'rgba(0,0,0,1)');   // hold solid most of the way
      vignette.addColorStop(0.80, 'rgba(0,0,0,0.7)'); // gentle start of fade
      vignette.addColorStop(1,    'rgba(0,0,0,0)');   // fully transparent at edge
      ctx.fillStyle = vignette;
      ctx.fillRect(0, 0, ANIM_W, ANIM_H);
      ctx.globalCompositeOperation = 'source-over';   // restore default

      rafId = requestAnimationFrame(loop);
    };
    loop();

    const texture = new THREE.CanvasTexture(canvas);
    texRef.current = texture;
    setTex(texture);

    return () => {
      cancelAnimationFrame(rafId);
      texture.dispose();
      texRef.current = null;
    };
  }, [drawFn]);

  // Mark the texture dirty every frame so Three.js re-uploads the latest pixels
  useFrame(() => {
    if (texRef.current) texRef.current.needsUpdate = true;
  });

  if (!drawFn || !tex) return null;   // don't render until texture is ready (avoids white flash)

  // Plane size: 0.72 × 0.34 world units at sphere radius 2.
  // Keeps the animation footprint roughly country-sized when zoomed in,
  // without swamping the whole visible area.  Aspect ratio 420:200 = 2.1:1.
  return (
    <mesh position={pos} quaternion={quat}>
      <planeGeometry args={[0.72, 0.34]} />
      <meshBasicMaterial
        map={tex}
        transparent
        depthWrite={false}
        toneMapped={false}
      />
    </mesh>
  );
}

// ── Globe scene ───────────────────────────────────────────────────────────────

function GlobeScene({ countries, onPick, zoomed, zoomTarget, selectedCountryName, onLandmarkClick, flying, planeGeoRef, onPlaneLand }) {
  const ctrlRef     = useRef();
  const earthRotRef = useRef(0);   // written by EarthVisuals, read by ClickCatcher + markers
  const markersRef  = useRef();

  useFrame(() => {
    if (ctrlRef.current) ctrlRef.current.enabled = !zoomed && !flying;
    // Keep markers group locked to earth rotation every frame
    if (markersRef.current) markersRef.current.rotation.y = earthRotRef.current;
  });

  return (
    <>
      <OrbitControls ref={ctrlRef} enablePan={false} minDistance={3} maxDistance={12} />

      {/* Always-present click target — outside Suspense */}
      <ClickCatcher
        countries={countries}
        onPick={onPick}
        earthRotRef={earthRotRef}
        zoomed={zoomed || flying}
      />

      {/* Stylized earth GLB — suspends until downloaded from backend */}
      <Suspense fallback={
        <mesh>
          <sphereGeometry args={[SPHERE_RADIUS, 64, 32]} />
          <meshStandardMaterial color="#1a3a6a" />
        </mesh>
      }>
        <EarthVisuals zoomed={zoomed || flying} earthRotRef={earthRotRef} />
      </Suspense>

      {/* Islands + landmarks — group rotates in sync with the earth */}
      <group ref={markersRef}>
        <WorldMarkers onLandmarkClick={onLandmarkClick} />
        {flying && (
          <Suspense fallback={null}>
            <PlaneRider geoRef={planeGeoRef} onLand={onPlaneLand} />
          </Suspense>
        )}
      </group>

      {/* Atmosphere glow */}
      <mesh>
        <sphereGeometry args={[2.09, 64, 32]} />
        <meshStandardMaterial
          color="#3366ff"
          transparent opacity={0.07}
          side={THREE.FrontSide}
          depthWrite={false}
        />
      </mesh>

      {/* Country animation overlay — inline R3F scene if available, else 2D canvas */}
      {zoomed && zoomTarget && selectedCountryName && (
        COUNTRY_SCENES[selectedCountryName]
          ? <CountryScene3DOverlay point={zoomTarget} countryName={selectedCountryName} />
          : <CountryOverlay        point={zoomTarget} countryName={selectedCountryName} />
      )}
    </>
  );
}

// ── Public component ──────────────────────────────────────────────────────────

export default function Globe({ onCountryClick, onLandmarkClick, zoomed, zoomTarget, selectedCountryName, searchTrigger }) {
  const [countries, setCountries] = useState([]);
  const [flying, setFlying] = useState(false);
  const planeGeoRef = useRef(null);   // { lat, lng, heading } — written by PlaneRider

  useEffect(() => {
    fetch('/countries.json')
      .then(r => r.json())
      .then(setCountries)
      .catch(err => {
        console.error('Failed to load countries.json:', err);
        logClientError(err, { type: 'countries-json-load' });
      });
  }, []);

  // When the search box selects a country, compute its 3D surface point and
  // fire onCountryClick exactly as if the user had tapped the globe directly.
  useEffect(() => {
    if (!searchTrigger) return;
    const point = latLngTo3D(searchTrigger.lat, searchTrigger.lng, SPHERE_RADIUS);
    onCountryClick(searchTrigger.code, searchTrigger.name, point);
    // Depend only on searchTrigger: this fires once per new search selection.
    // Including onCountryClick would re-fire whenever its identity changes.
  }, [searchTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePick = useCallback((country, point) => {
    onCountryClick(country.code, country.name, point);
  }, [onCountryClick]);

  // Land the plane: nearest country to where Beary is flying
  const handlePlaneLand = useCallback(() => {
    const g = planeGeoRef.current;
    if (!g || !countries.length) return;
    const country = nearestCountry(g.lat, g.lng, countries);
    if (country) {
      setFlying(false);
      onCountryClick(country.code, country.name, latLngTo3D(country.lat, country.lng, SPHERE_RADIUS));
    }
  }, [countries, onCountryClick]);

  const srOnly = {
    position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
    overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0,
  };

  return (
    <>
      <Canvas
        camera={{ position: [0, 0, 6], fov: 45 }}
        style={{ background: '#000010' }}
        aria-label="Interactive 3D globe. Tap a country to explore, or use the search box and the country list below."
      >
      <ambientLight intensity={0.5} />
      <directionalLight position={[10, 5, 8]} intensity={2.5} />
      <Stars radius={100} depth={50} count={5000} factor={4} fade />
      <CameraZoom target={zoomTarget} active={zoomed} />
      <GlobeScene
        countries={countries}
        onPick={handlePick}
        zoomed={zoomed}
        zoomTarget={zoomTarget}
        onLandmarkClick={onLandmarkClick}
        selectedCountryName={selectedCountryName}
        flying={flying}
        planeGeoRef={planeGeoRef}
        onPlaneLand={handlePlaneLand}
      />
    </Canvas>
      {/* Flight HUD */}
      {!zoomed && (
        <div style={hud.wrap}>
          {!flying ? (
            <button type="button" style={hud.flyBtn} onClick={() => setFlying(true)}>
              ✈️ Fly with Beary
            </button>
          ) : (
            <>
              <div style={hud.hint} aria-hidden="true">⬅️ ➡️ steer</div>
              <button type="button" style={hud.flyBtn} onClick={handlePlaneLand}>
                🪂 Land here!
              </button>
              <button type="button" style={hud.stopBtn} onClick={() => setFlying(false)}>
                Stop flying
              </button>
            </>
          )}
        </div>
      )}
      <nav style={srOnly} aria-label="Browse all countries">
        <details>
          <summary>Browse all countries</summary>
          <ul>
            {countries.map((c) => (
              <li key={c.code}>
                <button type="button" onClick={() => handlePick(c, latLngTo3D(c.lat, c.lng, SPHERE_RADIUS))}>
                  {c.name}
                </button>
              </li>
            ))}
          </ul>
        </details>
      </nav>
    </>
  );
}

// ── Flight HUD styles ─────────────────────────────────────────────────────────
const hud = {
  wrap: {
    position: 'fixed',
    right: 18,
    bottom: 118,
    zIndex: 30,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 10,
  },
  flyBtn: {
    border: '3px solid rgba(255,255,255,0.9)',
    borderRadius: 999,
    padding: '13px 22px',
    fontSize: 17,
    fontWeight: 800,
    color: '#fff',
    background: 'linear-gradient(135deg, #1D73C2, #57B04A)',
    boxShadow: '0 10px 24px rgba(4, 26, 52, 0.45)',
    cursor: 'pointer',
    letterSpacing: '-0.01em',
  },
  stopBtn: {
    border: 'none',
    borderRadius: 999,
    padding: '9px 16px',
    fontSize: 14,
    fontWeight: 700,
    color: '#cbd5e1',
    background: 'rgba(15, 27, 53, 0.72)',
    cursor: 'pointer',
  },
  hint: {
    borderRadius: 999,
    padding: '7px 14px',
    fontSize: 15,
    fontWeight: 700,
    color: '#e2e8f0',
    background: 'rgba(15, 27, 53, 0.72)',
  },
};
